/**
 * Slice 19a — Link an existing indexed post to a template (trainer+ only).
 *
 * Surfaces a compact "Set template" picker on each indexed-ad card so trainers can
 * walk the index after creating a template and tag matching posts. Each link counts
 * toward the org's `min_exemplars_for_template_signoff` threshold, so this is the
 * primary way to close the gap between a freshly-curated template and sign-off.
 */
import { useState } from "react";
import { patchAdsSocialPost } from "@/lib/adsAssistantClient";
import type { AdsAssistantSocialPostRow, AdsAssistantTemplateRow } from "@/lib/adsAssistantClient";

type Props = {
  post: AdsAssistantSocialPostRow;
  templates: AdsAssistantTemplateRow[];
  currentTemplateName: string | null;
  onChanged?: () => void;
};

export function AdsAssistantPostTemplatePicker({ post, templates, currentTemplateName, onChanged }: Props) {
  const activeTemplates = templates.filter((t) => t.is_active);
  const [selected, setSelected] = useState<string>(post.template_id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!activeTemplates.length) return null;

  const hasChange = (post.template_id ?? "") !== selected;

  async function commit() {
    setError(null);
    setBusy(true);
    const r = await patchAdsSocialPost(post.id, { template_id: selected ? selected : null });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onChanged?.();
  }

  const summaryLabel = currentTemplateName ? `Template: ${currentTemplateName}` : "Set template";

  return (
    <details className="text-[11px] text-gray-700 dark:text-gray-300">
      <summary className="cursor-pointer font-medium text-gray-700 hover:underline dark:text-gray-300 [&::-webkit-details-marker]:hidden">
        {summaryLabel} ▾
      </summary>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <select
          className="rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] dark:border-zinc-600 dark:bg-zinc-900 dark:text-gray-100"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={busy}
          aria-label="Pick template for this post"
        >
          <option value="">(no template)</option>
          {activeTemplates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {t.trainer_pattern_signed_off ? " ✓" : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!hasChange || busy}
          className="rounded border border-blue-700 px-1.5 py-1 text-[11px] font-medium text-blue-800 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-500 dark:text-blue-300 dark:hover:bg-blue-950/40"
          onClick={() => void commit()}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        {error ? (
          <span className="text-amber-800 dark:text-amber-300/90" role="alert">
            {error}
          </span>
        ) : null}
      </div>
    </details>
  );
}
